import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../Layout';
import ProductCatalog from '../ProductCatalog';
import { useAuth } from '../../auth/AuthContext';

const API = 'https://api.kallabakari.is';
const minDate = new Date().toISOString().split('T')[0];

function getPickupSlots(dateStr) {
  if (!dateStr) return [];
  const date = new Date(dateStr);
  const day = date.getDay();
  const isWeekend = day === 0 || day === 6;
  const startMin = isWeekend ? 8 * 60 : 7 * 60;
  const endMin = isWeekend ? 15 * 60 : 16 * 60;
  const slots = [];
  for (let m = startMin; m <= endMin; m += 30) {
    const h = String(Math.floor(m / 60)).padStart(2, '0');
    const min = String(m % 60).padStart(2, '0');
    slots.push(`${h}:${min}`);
  }
  return slots;
}

function formatProductLine(p) {
  switch (p.type) {
    case 'cake':     return `${p.details.cake}${p.details.size ? ` (${p.details.size})` : ''}`;
    case 'bread':    return `${p.details.bread} × ${p.details.quantity}`;
    case 'minidonut':return `Minidonuts × ${p.details.quantity}`;
    case 'bakkelsi': return `${p.details.name} × ${p.details.quantity}`;
    case 'salat':    return `${p.details.name} × ${p.details.quantity}`;
    case 'supa':     return `Súpa dagsins × ${p.details.quantity}`;
    default:         return `${p.details.name || p.details.other || 'Vara'} × ${p.details.quantity ?? 1}`;
  }
}

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-stone-400';
const Label = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{children}</label>
);

export default function NewOrder() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [date, setDate] = useState(searchParams.get('date') || '');
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [pickupTime, setPickupTime] = useState('');
  const [products, setProducts] = useState([]);
  const [note, setNote] = useState('');
  const [image, setImage] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const needsImage = products.some(p => p.type === 'cake' && p.details?.needsImage);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) return setError('Veldu dagsetningu');
    if (!pickupTime) return setError(`Veldu ${deliveryType === 'delivery' ? 'afhendingartíma' : 'sóttnartíma'}`);
    if (products.length === 0) return setError('Bættu við að minnsta kosti einni vöru');
    if (needsImage && !image) return setError('Vinsamlegast veldu mynd fyrir köku með mynd');
    setError('');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('date', date);
      formData.append('deliveryType', deliveryType);
      formData.append('pickupTime', pickupTime);
      formData.append('products', JSON.stringify(products));
      formData.append('note', note);
      if (image) formData.append('image', image);
      await axios.post(
        `${API}/api/company/orders`,
        formData,
        { headers: { Authorization: token } }
      );
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Villa kom upp');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">← Til baka</button>
        <h1 className="text-xl font-semibold text-gray-900">Ný pöntun</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="flex gap-6 items-start">

          {/* Left: order info + summary */}
          <div className="w-72 flex-shrink-0 space-y-4 sticky top-8">

            {/* Delivery info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Afhending</h2>

              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                {[{ value: 'pickup', label: 'Sótt' }, { value: 'delivery', label: 'Sent' }].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDeliveryType(opt.value)}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      deliveryType === opt.value ? 'bg-stone-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div>
                <Label>Dagsetning</Label>
                <input
                  className={inputCls}
                  type="date"
                  min={minDate}
                  value={date}
                  onChange={e => { setDate(e.target.value); setPickupTime(''); }}
                  required
                />
              </div>

              <div>
                <Label>{deliveryType === 'delivery' ? 'Afhendingartími' : 'Sóttnartími'}</Label>
                <select className={inputCls} value={pickupTime} onChange={e => setPickupTime(e.target.value)} disabled={!date}>
                  <option value="">{date ? 'Veldu tíma' : 'Veldu fyrst dagsetningu'}</option>
                  {getPickupSlots(date).map(slot => <option key={slot} value={slot}>{slot}</option>)}
                </select>
              </div>

              <div>
                <Label>Athugasemdir</Label>
                <textarea className={`${inputCls} resize-none`} rows={2} value={note} onChange={e => setNote(e.target.value)} />
              </div>

              {needsImage && (
                <div>
                  <Label>Mynd fyrir köku <span className="normal-case font-normal text-red-400">*nauðsynlegt</span></Label>
                  <label className="flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-stone-200 rounded-lg px-4 py-4 cursor-pointer hover:border-stone-400 transition-colors bg-stone-50">
                    <span className="text-xs text-gray-500">{image ? image.name : 'Smelltu til að velja mynd'}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => setImage(e.target.files?.[0] ?? null)} />
                  </label>
                  {image && (
                    <img src={URL.createObjectURL(image)} alt="Forskoðun" className="mt-2 w-24 h-24 object-cover rounded-lg border border-stone-100" />
                  )}
                </div>
              )}
            </div>

            {/* Order summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Pöntun</h2>
              {products.length === 0 ? (
                <p className="text-xs text-gray-400">Engar vörur valdar</p>
              ) : (
                <ul className="space-y-2">
                  {products.map((p, i) => (
                    <li key={i} className="flex items-start justify-between gap-2 text-xs text-gray-700 bg-stone-50 border border-stone-100 rounded-lg px-3 py-2">
                      <span className="flex-1">{formatProductLine(p)}</span>
                      <button
                        type="button"
                        onClick={() => setProducts(prev => prev.filter((_, j) => j !== i))}
                        className="text-red-300 hover:text-red-500 flex-shrink-0 transition-colors"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting || products.length === 0}
              className="w-full bg-stone-500 hover:bg-stone-600 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
            >
              {submitting ? 'Vista...' : 'Vista pöntun'}
            </button>
          </div>

          {/* Right: product catalog */}
          <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Veldu vörur</h2>
            <ProductCatalog onAdd={p => setProducts(prev => [...prev, p])} />
          </div>

        </div>
      </form>
    </Layout>
  );
}
